import {Injectable} from '@angular/core';
import {Copy, ProductCopy} from '@ox/typings';
import {ShopDataService} from '../shop-data';
import {BehaviorSubject, map, takeWhile, tap} from 'rxjs';
import {replaceVariable} from '@ox/utils';

/** Service for formatting all copy from the Copy API */
@Injectable()
export class CopyFormattingService {

  private isCopyRefreshing = false;
  private copy = new BehaviorSubject<Copy | undefined>(undefined);
  copy$ = this.copy.asObservable().pipe(
    tap((copy) => {
      // if copy does not exist, recall it from shop-data.service
      if (!copy) {
        this.refreshCopy();
      }
    })
  );

  constructor(private shopDataService: ShopDataService) {
    this.refreshCopy();
  }

  matchProductsById(products: any, copies: any) {
    const matchId = (product: any, copies: any) => {
      //Use the code property for most accurate match, if not availble, then fall back to the old implementation
      if (product.code) {
        return copies.find((copy: any) => copy.code === product.code);
      } else {
        return copies.find((copy: any) => copy.name === product.id);
      }
    };

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      products[i].copy = matchId(product, copies);
    }
    return products;
  }

  getProductCopy(copy: Copy, planCode: string): ProductCopy | undefined {
    return copy.products?.find((product) => product.code === planCode);
  }

  getProductCopyByName(copy: Copy, productName: string): ProductCopy | undefined {
    return copy.products?.find((product) => product.displayName === productName);
  }

  getGenericCopy(id: string, copyData?: Copy, variableMap: Record<string, string> = {}): string | undefined {
    const str = copyData?.generic?.find((copy: any) => copy.copyDocKey === id)?.copyDocValue;
    return str ? replaceVariable(str, variableMap) : str;
  }
  getGenericCopy$(id: string, variableMap: Record<string, string> = {}) {
    return this.copy$.pipe(map((copy) => this.getGenericCopy(id, copy, variableMap)));
  }

  getHelpContentByName(copy: Copy, name: string) {
    const helpContent = copy.helpContents?.find(
      (content) => content.name?.toLocaleLowerCase() === name.toLocaleLowerCase()
    );

    if (helpContent === undefined) {
      return undefined;
    }

    return helpContent;
  }

  refreshCopy() {
    // Prevent recalling the copy API if it is already in progress
    if (this.isCopyRefreshing) return;
    this.isCopyRefreshing = true;
    this.shopDataService.getCopyData()
      .pipe(takeWhile((val) => !val, true))
      .subscribe({
        next: (copy) => this.copy.next(copy),
        complete: () => this.isCopyRefreshing = false
    });
  }
}
